<template>
  <div class="dubbing-container">
    <div class="header-section">
      <h2>藏语趣配音 <span class="title-tibetan">བོད་སཀད་སྒྲ་སྦྱོར།</span></h2>
      <p class="subtitle">为藏戏片段配音，体验藏族文化魅力<br><span class="tibetan">བོད་ཞབས་བརྙན་ཐུམ་ལ་སྒྲ་སྦྱོར་བྱེད་པ།</span></p>
    </div>
    
    <div class="content-grid">
      <div class="video-section">
        <h3>选择配音片段 <span class="section-tibetan">སྒྲ་སྦྱོར་བརྙན་ཐུམ་འདེམས།</span></h3>
        <el-select v-model="selectedVideo" placeholder="选择视频片段" @change="loadVideo">
          <el-option
            v-for="video in videoList"
            :key="video.id"
            :label="video.title"
            :value="video.id"
          />
        </el-select>
        
        <div class="video-player" v-if="currentVideo">
          <video ref="videoPlayerRef" controls :key="currentVideo.id">
            <source :src="currentVideo.url" type="video/mp4">
          </video>
          <div class="video-info">
            <h4>{{ currentVideo.title }}</h4>
            <p>{{ currentVideo.description }}</p>
          </div>
        </div>
      </div>
      
      <div class="recording-section">
        <h3>录制配音 <span class="section-tibetan">སྒྲ་སྦྱོར་བརྙན་ལེན།</span></h3>
        
        <div class="recording-controls">
          <el-button 
            type="primary" 
            :icon="Microphone"
            @click="startRecording"
            :disabled="isRecording"
            size="large"
          >
            开始录音 <span class="btn-tibetan">སྒྲ་ལེན།</span>
          </el-button>
          
          <el-button 
            type="danger"
            @click="stopRecording"
            :disabled="!isRecording"
            size="large"
          >
            停止录音 <span class="btn-tibetan">མཚམས།</span>
          </el-button>
          
          <div class="recording-status" v-if="isRecording">
            <el-icon class="recording-icon"><VideoCamera /></el-icon>
            <span>录音中... {{ recordingTime }}s</span>
          </div>
        </div>
        
        <div class="audio-list" v-if="recordings.length > 0">
          <h4>我的配音作品</h4>
          <div 
            v-for="(recording, index) in recordings" 
            :key="index"
            class="audio-item"
          >
            <div class="audio-info">
              <span class="audio-name">配音 {{ index + 1 }}</span>
              <span class="audio-time">{{ formatTime(recording.duration) }}</span>
            </div>
            <audio :src="recording.url" controls></audio>
            <div class="audio-actions">
              <el-button type="success" size="small" @click="uploadRecording(recording)">
                <el-icon><Upload /></el-icon>
                上传
              </el-button>
              <el-button type="danger" size="small" @click="deleteRecording(index)">
                <el-icon><Delete /></el-icon>
                删除
              </el-button>
            </div>
          </div>
        </div>
        
        <div class="tips">
          <h4>配音小贴士 <span class="section-tibetan">སལོབ་བསྟན།</span></h4>
          <ul>
            <li>请在安静的环境中录音，确保音质清晰</li>
            <li>可以先观看视频，熟悉台词和节奏</li>
            <li>藏语发音要准确，注意语调和情感表达</li>
            <li>录音完成后可以试听，不满意可以重新录制</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted, computed, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Microphone, VideoCamera, Upload, Delete } from '@element-plus/icons-vue'
import { uploadAudio } from '@/api'

const videoPlayerRef = ref(null)
const selectedVideo = ref(null)
const isRecording = ref(false)
const recordingTime = ref(0)
const recordings = ref([])
let mediaRecorder = null
let recordingInterval = null
let audioChunks = []

const videoList = ref([
  {
    id: 1,
    title: '《白玛雯巴》经典片段1',
    url: '/api/uploads/map/p1.mp4',
    description: '经典片段1主要讲述了父难、诞生、知仇。国王穆迪杰布逼富商诺布桑布下海寻宝，商人遇难。遗腹子 白玛雯巴 出生、长大，得知父亲死因，立誓报仇。国王发现他，强令去 龙宫 取宝。'
  },
  {
    id: 2,
    title: '《白玛雯巴》经典片段2',
    url: '/api/uploads/map/p2.mp4',
    description: '经典片段2主要讲述了龙宫取宝、归来。白玛雯巴得空行母相助，入海龙宫，战胜龙王，取得宝物。平安回宫献宝，国王暂时放过他，但贪心更盛。'
  },
  {
    id: 3,
    title: '《白玛雯巴》经典片段3',
    url: '/api/uploads/map/p3.mp4',
    description: '经典片段3主要讲述了罗刹国历险、夺金锅。国王再逼他去 罗刹国 取 飞天金锅、银匙。白玛雯巴念咒制服 铁水罗刹、九头罗刹女王，取回金锅，并把五位罗刹女超度为仙女，一同带回。'
  },
  {
    id: 4,
    title: '《白玛雯巴》经典片段4',
    url: '/api/uploads/map/p4.mp4',
    description: '经典片段4主要讲述了火烧、复活、诱王。国王霸占宝物、仙女，将白玛雯巴扔进火里烧死。五位仙女以法力让他从莲花中复活。白玛雯巴假意献金锅，引诱国王坐上飞天金锅。'
  },
  {
    id: 5,
    title: '《白玛雯巴》经典片段5',
    url: '/api/uploads/map/p5.mp4',
    description: '经典片段5主要讲述了惩恶、登基、圆满。白玛雯巴驾金锅飞至 罗刹国，把国王、奸臣扔下喂罗刹。百姓拥戴，白玛雯巴登基为王，国泰民安，剧终。'
  },
  {
    id: 6,
    title: '《诺桑王子》经典片段',
    url: '/api/uploads/map/p6.mp4',
    description: '觉木隆藏戏《诺桑王子》（《诺桑法王》） 是八大藏戏之首、最古老、流传最广的经典神话爱情剧，也是觉木隆流派的核心看家戏。它以华丽婉转的唱腔、细腻抒情的表演、绚烂的舞台著称，完美体现了觉木隆 “唱腔宝库、表演灵动” 的艺术风格。'
  },
  {
    id: 7,
    title: '《嘉乐达让》经典片段',
    url: '/api/uploads/map/p7.mp4',
    description: '江嘎尔藏戏《嘉乐达让》（亦作《甲鲁达让》《嘉洛达让》）是江嘎尔藏戏的核心看家戏，与《诺桑法王》并列，同属江嘎尔标志性传统剧目。剧目取材自藏族民间传说与《格萨尔》史诗相关的嘉洛（嘉乐）部落传说，围绕嘉乐部落首领达让的传奇人生展开。核心情节围绕部落兴衰、家族荣誉、正义斗争展开，通过宏大的叙事与繁复的唱腔，刻画达让的智勇担当，以及部落内部的团结、与外部势力的博弈，最终传递出守护家园、坚守道义的主题。'
  },
  {
    id: 8,
    title: '觉木隆藏戏唱腔',
    url: '/api/uploads/map/p8.mp4',
    description: '觉木隆藏戏是蓝面具藏戏中唱腔最丰富、旋律最华丽、表现力最强的流派，被誉为藏戏 “唱腔宝库”，整体风格婉转悠扬、起伏大、花腔多、帮腔响亮，是当代流传最广的藏戏唱腔体系。'
  },
])

const currentVideo = computed(() => {
  if (!selectedVideo.value) return null
  return videoList.value.find(v => v.id === selectedVideo.value) || null
})

const loadVideo = async () => {
  await nextTick()
  if (videoPlayerRef.value) {
    try {
      videoPlayerRef.value.load()
    } catch (_) {
    }
  }
  if (currentVideo.value) {
    ElMessage.success('视频已加载')
  }
}

const startRecording = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    mediaRecorder = new MediaRecorder(stream)
    audioChunks = []
    
    mediaRecorder.ondataavailable = (event) => {
      audioChunks.push(event.data)
    }
    
    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/wav' })
      const audioUrl = URL.createObjectURL(audioBlob)
      recordings.value.push({
        url: audioUrl,
        blob: audioBlob,
        duration: recordingTime.value,
        timestamp: Date.now()
      })
      recordingTime.value = 0
    }
    
    mediaRecorder.start()
    isRecording.value = true
    
    recordingInterval = setInterval(() => {
      recordingTime.value++
    }, 1000)
    
    ElMessage.success('开始录音')
  } catch (error) {
    ElMessage.error('无法访问麦克风，请检查权限设置')
  }
}

const stopRecording = () => {
  if (mediaRecorder && isRecording.value) {
    mediaRecorder.stop()
    mediaRecorder.stream.getTracks().forEach(track => track.stop())
    isRecording.value = false
    clearInterval(recordingInterval)
    ElMessage.success('录音已停止')
  }
}

const uploadRecording = async (recording) => {
  try {
    const formData = new FormData()
    formData.append('audio', recording.blob, `dubbing_${recording.timestamp}.wav`)
    formData.append('videoId', selectedVideo.value)
    
    await uploadAudio(formData)
    ElMessage.success('上传成功')
  } catch (error) {
    ElMessage.error('上传失败')
  }
}

const deleteRecording = (index) => {
  recordings.value.splice(index, 1)
  ElMessage.success('已删除')
}

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

onUnmounted(() => {
  if (isRecording.value) {
    stopRecording()
  }
})
</script>

<style scoped lang="scss">
.dubbing-container {
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header-section {
  margin-bottom: 30px;
  
  h2 {
    color: #8B4513;
    font-size: 28px;
    margin-bottom: 10px;

    .title-tibetan {
      font-size: 17px;
      color: #A0522D;
      font-weight: 400;
      opacity: 0.7;
    }
  }
  
  .subtitle {
    color: #666;
    font-size: 16px;
    line-height: 1.6;

    .tibetan {
      font-size: 14px;
      opacity: 0.7;
    }
  }
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  h3 {
    color: #8B4513;
    margin-bottom: 15px;
    font-size: 20px;

    .section-tibetan {
      font-size: 14px;
      color: #A0522D;
      font-weight: 400;
      opacity: 0.7;
    }
  }

  .btn-tibetan {
    font-size: 13px;
    opacity: 0.8;
    margin-left: 4px;
  }
}

.video-section {
  .video-player {
    margin-top: 20px;
    
    video {
      width: 100%;
      border-radius: 8px;
      background: #000;
    }
    
    .video-info {
      margin-top: 15px;
      padding: 15px;
      background: #f9f9f9;
      border-radius: 8px;
      
      h4 {
        color: #8B4513;
        margin-bottom: 8px;
      }
      
      p {
        color: #666;
        line-height: 1.6;
      }
    }
  }
}

.recording-section {
  .recording-controls {
    display: flex;
    gap: 15px;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    
    .recording-status {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #f56c6c;
      font-weight: bold;
      
      .recording-icon {
        animation: pulse 1.5s infinite;
      }
    }
  }
  
  .audio-list {
    margin-top: 30px;
    
    h4 {
      color: #8B4513;
      margin-bottom: 15px;
    }
    
    .audio-item {
      background: #f9f9f9;
      padding: 15px;
      border-radius: 8px;
      margin-bottom: 15px;
      
      .audio-info {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
        
        .audio-name {
          font-weight: bold;
          color: #333;
        }
        
        .audio-time {
          color: #999;
          font-size: 14px;
        }
      }
      
      audio {
        width: 100%;
        margin-bottom: 10px;
      }
      
      .audio-actions {
        display: flex;
        gap: 10px;
      }
    }
  }
  
  .tips {
    margin-top: 30px;
    padding: 20px;
    background: #fff7e6;
    border-radius: 8px;
    border-left: 4px solid #D2691E;
    
    h4 {
      color: #8B4513;
      margin-bottom: 10px;
    }
    
    ul {
      margin-left: 20px;
      
      li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 5px;
      }
    }
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style>
